import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { Lock, LogOut, Download, RefreshCw, Loader2, Users, FileBarChart, CreditCard, KeyRound, DollarSign } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_STORAGE = "fp_admin_token";

export default function AdminDashboard() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_STORAGE) || "");
  const [stats, setStats] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const fetchAll = useCallback(async (t) => {
    if (!t) return;
    setLoading(true);
    try {
      const [s, tx] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${t}` } }),
        axios.get(`${API}/admin/transactions`, { headers: { Authorization: `Bearer ${t}` }, params: { limit: 50 } }),
      ]);
      setStats(s.data);
      setTransactions(tx.data || []);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Sesi admin expired. Login ulang.");
        setToken("");
        localStorage.removeItem(TOKEN_STORAGE);
      } else {
        toast.error("Gagal load data: " + (err?.response?.data?.detail || err.message));
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) fetchAll(token);
  }, [token, fetchAll]);

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_STORAGE);
    setToken("");
    setStats(null);
    setTransactions([]);
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const resp = await axios.get(`${API}/admin/export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([resp.data]));
      const today = new Date().toISOString().slice(0, 10);
      const a = document.createElement("a");
      a.href = url;
      a.download = `audit-report-${today}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Excel berhasil di-download.");
    } catch (err) {
      toast.error("Gagal export: " + (err?.response?.data?.detail || err.message));
    } finally {
      setDownloading(false);
    }
  };

  if (!token) {
    return <AdminLogin onSuccess={(t) => { localStorage.setItem(TOKEN_STORAGE, t); setToken(t); }} />;
  }

  return (
    <div className="brutal min-h-screen bg-[#fdfdfd] text-[#0a0a0a]" data-testid="admin-dashboard">
      <Toaster richColors position="top-center" />
      <header className="border-b-2 border-black bg-black text-white">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-20 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-white text-black w-10 h-10 flex items-center justify-center border-2 border-white font-display font-black">FP</div>
            <div>
              <div className="font-display font-black uppercase tracking-tight text-lg">Admin Dashboard</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#aaa]">Fakta Produk · Internal</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => fetchAll(token)}
              disabled={loading}
              className="border-2 border-white bg-transparent px-3 py-2 font-mono text-xs uppercase tracking-[0.14em] hover:bg-[#FFD700] hover:text-black hover:border-[#FFD700] disabled:opacity-50 inline-flex items-center gap-2"
              data-testid="admin-refresh"
            >
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              Refresh
            </button>
            <button
              type="button"
              onClick={handleLogout}
              className="border-2 border-white bg-transparent px-3 py-2 font-mono text-xs uppercase tracking-[0.14em] hover:bg-[#FF2400] hover:border-[#FF2400] inline-flex items-center gap-2"
              data-testid="admin-logout"
            >
              <LogOut className="w-3.5 h-3.5" />
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-20 py-10">
        {!stats ? (
          <div className="text-center py-20 font-mono uppercase tracking-[0.2em] text-[#666]">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3" />
            Memuat data...
          </div>
        ) : (
          <>
            {/* Action bar */}
            <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
              <div>
                <div className="font-mono text-xs uppercase tracking-[0.2em] text-[#666]">/ 01 — Overview</div>
                <h1 className="font-display font-black uppercase tracking-tighter text-4xl md:text-5xl mt-2 leading-[0.95]">
                  Statistik Utama.
                </h1>
              </div>
              <button
                type="button"
                onClick={handleDownload}
                disabled={downloading}
                className="border-2 border-black bg-[#0a0a0a] text-[#FFD700] px-5 py-3 font-mono font-bold uppercase tracking-[0.18em] text-sm hover:bg-[#FFD700] hover:text-black disabled:opacity-50 inline-flex items-center gap-2 shadow-brutal"
                data-testid="admin-download-button"
              >
                {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                Download All Database (Excel)
              </button>
            </div>

            {/* Stat cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
              <StatCard
                icon={Users}
                label="Visitors Today"
                value={stats.visitors_today}
                hint="Unique IP+UA per day"
                color="bg-[#002FA7] text-white"
                testid="stat-visitors"
              />
              <StatCard
                icon={FileBarChart}
                label="Total Audits"
                value={stats.total_audits}
                hint={`${stats.total_audits_all} total (incl. clarification)`}
                color="bg-[#FFD700] text-[#0a0a0a]"
                testid="stat-audits"
              />
              <StatCard
                icon={DollarSign}
                label="Total Revenue"
                value={`Rp ${(stats.total_revenue_idr || 0).toLocaleString("id-ID")}`}
                hint={`${stats.paid_transactions || 0} transaksi paid`}
                color="bg-[#0a8a3a] text-white"
                testid="stat-revenue"
              />
              <StatCard
                icon={CreditCard}
                label="Payment Tx"
                value={stats.total_transactions || 0}
                hint={`${stats.pending_transactions || 0} pending · ${stats.paid_transactions || 0} paid`}
                color="bg-white text-[#0a0a0a] border-[#0a0a0a]"
                testid="stat-tx"
              />
              <StatCard
                icon={KeyRound}
                label="Premium Keys"
                value={stats.active_premium_keys || 0}
                hint={`${stats.total_premium_keys || 0} total all-time · aktif`}
                color="bg-[#0a0a0a] text-[#FFD700]"
                testid="stat-keys"
              />
            </div>

            {/* Top audits */}
            <section className="mb-10">
              <div className="font-mono text-xs uppercase tracking-[0.2em] text-[#666] mb-2">/ 02 — Top Audits per Product</div>
              <div className="border-2 border-black overflow-x-auto" data-testid="top-audits-table">
                <table className="w-full font-sans text-sm">
                  <thead>
                    <tr className="bg-[#0a0a0a] text-white">
                      <th className="border-2 border-black px-3 py-2.5 text-left font-mono text-[11px] uppercase tracking-[0.18em]">#</th>
                      <th className="border-2 border-black px-3 py-2.5 text-left font-mono text-[11px] uppercase tracking-[0.18em]">Brand</th>
                      <th className="border-2 border-black px-3 py-2.5 text-left font-mono text-[11px] uppercase tracking-[0.18em]">Model</th>
                      <th className="border-2 border-black px-3 py-2.5 text-right font-mono text-[11px] uppercase tracking-[0.18em]">Audit Count</th>
                      <th className="border-2 border-black px-3 py-2.5 text-left font-mono text-[11px] uppercase tracking-[0.18em]">Last Audit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(stats.top_audits_per_product || []).map((d, i) => (
                      <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-[#f7f7f7]"}>
                        <td className="border-2 border-black px-3 py-2 font-mono text-xs text-[#666]">{i + 1}</td>
                        <td className="border-2 border-black px-3 py-2 font-bold uppercase">{d.brand}</td>
                        <td className="border-2 border-black px-3 py-2">{d.model}</td>
                        <td className="border-2 border-black px-3 py-2 text-right font-mono font-bold">{d.count}</td>
                        <td className="border-2 border-black px-3 py-2 font-mono text-xs text-[#666]">{(d.last || "").slice(0, 19).replace("T", " ")}</td>
                      </tr>
                    ))}
                    {(!stats.top_audits_per_product || stats.top_audits_per_product.length === 0) && (
                      <tr>
                        <td colSpan="5" className="border-2 border-black px-3 py-6 text-center text-[#666] font-mono uppercase tracking-[0.18em] text-xs">
                          Belum ada audit
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>

            {/* Payment Transactions */}
            <section className="mb-10">
              <div className="flex items-center justify-between mb-2">
                <div className="font-mono text-xs uppercase tracking-[0.2em] text-[#666]">/ 03 — Payment Transactions (50 terbaru)</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.18em] border-2 border-black px-2 py-0.5">
                  {transactions.length} entries
                </div>
              </div>
              <div className="border-2 border-black overflow-x-auto" data-testid="transactions-table">
                <table className="w-full font-sans text-sm">
                  <thead>
                    <tr className="bg-[#0a0a0a] text-white">
                      <th className="border-2 border-black px-3 py-2.5 text-left font-mono text-[11px] uppercase tracking-[0.18em]">Order ID</th>
                      <th className="border-2 border-black px-3 py-2.5 text-left font-mono text-[11px] uppercase tracking-[0.18em]">Email</th>
                      <th className="border-2 border-black px-3 py-2.5 text-right font-mono text-[11px] uppercase tracking-[0.18em]">Amount</th>
                      <th className="border-2 border-black px-3 py-2.5 text-left font-mono text-[11px] uppercase tracking-[0.18em]">Status</th>
                      <th className="border-2 border-black px-3 py-2.5 text-left font-mono text-[11px] uppercase tracking-[0.18em]">Key Granted</th>
                      <th className="border-2 border-black px-3 py-2.5 text-left font-mono text-[11px] uppercase tracking-[0.18em]">Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map((tx, i) => {
                      const statusColor = tx.business_status === "paid"
                        ? "bg-[#e0ffe5] text-[#0a8a3a] font-bold"
                        : tx.business_status === "pending"
                        ? "bg-[#fff8d6] text-[#0a0a0a]"
                        : "bg-[#ffe5e0] text-[#FF2400]";
                      return (
                        <tr key={tx.order_id || i} className={i % 2 === 0 ? "bg-white" : "bg-[#f7f7f7]"}>
                          <td className="border-2 border-black px-3 py-2 font-mono text-[11px]">{tx.order_id}</td>
                          <td className="border-2 border-black px-3 py-2 font-mono text-xs">{tx.email}</td>
                          <td className="border-2 border-black px-3 py-2 text-right font-bold">
                            Rp {(tx.amount || 0).toLocaleString("id-ID")}
                          </td>
                          <td className="border-2 border-black px-3 py-2">
                            <span className={`inline-block px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.18em] ${statusColor}`}>
                              {tx.business_status || "—"}
                            </span>
                          </td>
                          <td className="border-2 border-black px-3 py-2 font-mono text-xs">
                            {tx.premium_key_granted ? "✓" : <span className="text-[#aaa]">—</span>}
                          </td>
                          <td className="border-2 border-black px-3 py-2 font-mono text-xs text-[#666]">
                            {(tx.created_at || "").slice(0, 19).replace("T", " ")}
                          </td>
                        </tr>
                      );
                    })}
                    {transactions.length === 0 && (
                      <tr>
                        <td colSpan="6" className="border-2 border-black px-3 py-6 text-center text-[#666] font-mono uppercase tracking-[0.18em] text-xs">
                          Belum ada transaksi pembayaran
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>

            <div className="text-center font-mono text-[10px] uppercase tracking-[0.18em] text-[#999]">
              Generated: {stats.generated_at?.slice(0, 19).replace("T", " ")} UTC
            </div>
          </>
        )}
      </main>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, hint, color, testid }) {
  return (
    <div className={`border-2 border-black p-5 md:p-6 ${color}`} data-testid={testid}>
      <div className="flex items-center justify-between">
        <Icon className="w-5 h-5" strokeWidth={2.5} />
        <span className="font-mono text-[10px] uppercase tracking-[0.22em] opacity-80">{label}</span>
      </div>
      <div className="font-display font-black tracking-tighter text-5xl md:text-6xl mt-3 leading-none">
        {typeof value === "number" ? value.toLocaleString("id-ID") : value}
      </div>
      <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.18em] opacity-70">{hint}</div>
    </div>
  );
}

function AdminLogin({ onSuccess }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await axios.post(`${API}/admin/login`, { username, password });
      onSuccess(r.data.token);
      toast.success("Login berhasil.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login gagal.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="brutal min-h-screen bg-[#fdfdfd] grid place-items-center p-6">
      <Toaster richColors position="top-center" />
      <form
        onSubmit={submit}
        className="w-full max-w-md bg-white border-2 border-black p-6 md:p-8 shadow-brutal"
        data-testid="admin-login-form"
      >
        <div className="flex items-center gap-3 mb-5">
          <div className="bg-black text-[#FFD700] w-10 h-10 flex items-center justify-center border-2 border-black font-display font-black">FP</div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#666]">Admin Login</div>
            <h2 className="font-display font-black uppercase tracking-tight text-xl">Fakta Produk</h2>
          </div>
        </div>

        <label className="block font-mono text-[10px] uppercase tracking-[0.22em] text-[#666] mt-2">Username</label>
        <input
          type="text"
          required
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          className="mt-1 w-full px-3 py-2.5 border-2 border-black focus:outline-none font-sans"
          data-testid="admin-username-input"
          autoFocus
        />

        <label className="block font-mono text-[10px] uppercase tracking-[0.22em] text-[#666] mt-4">Password</label>
        <input
          type="password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="mt-1 w-full px-3 py-2.5 border-2 border-black focus:outline-none font-sans"
          data-testid="admin-password-input"
        />

        <button
          type="submit"
          disabled={loading || !username || !password}
          className="mt-6 w-full bg-black text-[#FFD700] px-5 py-3 font-mono font-bold uppercase tracking-[0.18em] text-sm border-2 border-black hover:bg-[#FFD700] hover:text-black disabled:opacity-50 inline-flex items-center justify-center gap-2 transition-colors"
          data-testid="admin-login-submit"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
          {loading ? "Verifikasi..." : "Login"}
        </button>

        <div className="mt-4 text-center font-mono text-[10px] uppercase tracking-[0.18em] text-[#999]">
          Akses hanya untuk administrator.
        </div>
      </form>
    </div>
  );
}
